/**
 * Виджеты категории "Оформление и контент": текст, изображения, цитаты,
 * погода, фигуры, iframe, фоновое изображение, валюты, холст.
 *
 * Факты — по New Tab Studio 4.0.0:
 * data/screenshots/report-4-navigation-design.md и report-4-new-widgets.md.
 *
 * @see docs/seo/SEO-STRATEGY.md
 * @see docs/SEO_WRITER.md
 */

export default [
	{
		slug: 'text',
		name: 'Text',
		category: 'Design',
		plan: 'free',
		model: 'TextModel',
		title: 'Text Widget for Chrome New Tab, 22 Fonts',
		metaDescription:
			'Put custom text on your Chrome new tab: a label, a goal, a greeting. 22 bundled fonts with Cyrillic, sizes up to 999, outline, shadow, borders and rotation.',
		h1: 'Text widget: custom text on your new tab',
		lead:
			'The text widget puts custom text on your Chrome new tab page, whether that’s a label over a countdown, a goal for the quarter or one word as the whole page. Type it, pick one of 22 fonts, drop it where it belongs.',
		screenshot: {
			file: 'text-fonts.webp',
			alt:
				'Eight text widgets in different fonts, each pairing an English and a Russian word, with the font name below',
			caption: 'Eight of the new fonts, each writing Latin and Cyrillic',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Labels make a layout readable',
				p: [
					'A countdown showing 14 days is a number. Put "Final exams" over it and it’s a warning. Most widgets read better with a word or two beside them, and giving them that word is this one’s day job.',
					'Its other job is the headline. A name, a greeting, the thing this page exists for, set large across the middle.',
				],
			},
			{
				h2: '22 fonts in five groups',
				p: [
					'Version 4.0 took the list from ten to 22 and grouped it by mood. Sans (Roboto Light, Roboto, Inter, Montserrat, Montserrat Alternates, Comfortaa, Exo 2), Display (Oswald, Unbounded, Russo One, Rubik Mono, Stalinist, Press Start, Poiret), Serif (Playfair, Cormorant), Handwriting (Caveat, Marck Script, Amatic SC, Lobster, Pacifico) and Monospace, which is JetBrains Mono.',
					'All of them ship inside the extension and cover Cyrillic as well as Latin. Nothing loads from a font service, so text renders offline, and a font file is read only when you apply it, one alphabet at a time. Chinese and Japanese fall back to your system font.',
					'Old themes set in Verdana, Impact or Comic Sans keep them. Those legacy fonts show up only on widgets that already use them.',
				],
				image: {
					file: 'text-font-menu.webp',
					alt:
						'Font list open from a text widget’s toolbar, fonts grouped as Sans, Display, Serif and Handwriting, each in its own style',
					caption: 'You see the font before you pick it',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Readable over any photo',
				p: [
					'White text on a photo works until the photo changes. The shadow button in Font style adds one tuned shadow. A background fill at 10 to 30 percent lifts the text without hiding the picture; at full opacity it becomes a card, fine on a dashboard and heavy on a landscape.',
					'New in 4.0 is Stroke, an outline round every letter, which holds up on busy photos where a shadow gives in. Underline and strikethrough sit in the same panel.',
					'What the text widget does not have is background blur. When the whole area behind the words is too loud, put a translucent shape under them.',
				],
			},
			{
				h2: 'Size, border, rotation',
				p: [
					'Font size offers presets from 10 to 288 and takes anything from 1 to 999. The Border panel adds a solid, dashed or dotted frame up to 12 px thick, with rounded corners. The rotate handle tilts the text and snaps to 15° steps unless you hold Alt.',
					'Keep it to a line or two. A new tab gets about a second of attention, and a paragraph gets skipped. Text you want to reread belongs in the notes widget.',
				],
				image: {
					file: 'style-panel.webp',
					alt:
						'Border panel open above a text label: solid line, 3 px thickness, 70% opacity, 20 px corners and a color palette',
					caption:
						'Line style, thickness, opacity, corners and color in one small panel',
					width: 1440,
					height: 900,
					small: true,
				},
			},
		],
		settings: [
			{
				name: 'Font',
				detail:
					'22 fonts under Sans, Display, Serif, Handwriting and Monospace, all with Cyrillic.',
			},
			{
				name: 'Font size',
				detail:
					'Presets from 10 to 288, or type any size from 1 to 999.',
			},
			{
				name: 'Font style',
				detail:
					'Bold, Italic, Underline, Strikethrough, Text shadow and Stroke.',
			},
			{ name: 'Alignment', detail: 'Left, Center or Right.' },
			{
				name: 'Colour and fill',
				detail:
					'Text colour, and a background fill you can fade to see-through.',
			},
			{
				name: 'Border',
				detail:
					'Solid, dashed or dotted, 0 to 12 px, with opacity and rounded corners.',
			},
			{
				name: 'Rotate',
				detail: 'From −180° to 180°, by button or by the handle.',
			},
		],
		useCases: [
			'Labels above a countdown, habit tracker or clock.',
			'A large headline naming the goal of the page.',
			'A name or greeting on a personal theme.',
			'Section headers on a dashboard-style layout.',
			'A single word, tilted a few degrees, as the whole page.',
		],
		faq: [
			{
				q: 'How do I put custom text on my Chrome new tab?',
				a:
					'Chrome’s own new tab has no place for text of your own, so custom text takes an extension. In New Tab Studio, the text widget puts any word or line on the page in one of 22 bundled fonts, at any size from 1 to 999, and you drag it wherever it belongs. A label, a goal, a greeting or your name all work.',
			},
			{
				q: 'Can I use my own font on the new tab?',
				a:
					'No, the text widget sticks to its bundled set of 22 fonts in five groups, from Inter and Oswald to Caveat and JetBrains Mono. Every one covers Latin and Cyrillic and ships inside the extension, so text appears instantly and works offline. Chinese and Japanese characters fall back to your system font.',
			},
			{
				q: 'How do I keep text readable over a changing wallpaper?',
				a:
					'Text stays readable over any wallpaper once it has a shadow, a Stroke outline or a translucent background fill, all found in the text widget’s toolbox. For a large busy area, slide a see-through shape widget underneath. The text widget has no background blur, so those are your tools.',
			},
			{
				q: 'Do I need PRO for more fonts?',
				a:
					'No, all 22 fonts and every style option in the text widget are free, Stroke and rotation included. What the free plan caps is how many widgets you add yourself (three), and text that came with an installed theme sits outside that count.',
			},
		],
		related: ['quotes', 'notes', 'countdown', 'figure'],
		keywords: [
			'text widget new tab',
			'custom text chrome new tab',
			'new tab page custom message',
		],
	},

	{
		slug: 'image',
		name: 'Image & GIF',
		category: 'Design',
		plan: 'free',
		model: 'ImageModel',
		title: 'Vision Board Chrome Extension, Image & GIF',
		metaDescription:
			'Build a vision board on your Chrome new tab from photos, GIFs and looping video. Tilt, frame and layer each picture; pull from Unsplash, GIPHY or wallhaven.',
		h1: 'Image & GIF widget: a vision board on every new tab',
		lead:
			'A vision board Chrome extension needs one thing, pictures you can place anywhere, and that’s what this widget is. Photos, a new tab GIF or a muted looping clip, each sized, tilted and stacked the way you want.',
		screenshot: {
			file: 'image-vision-board.webp',
			alt:
				'Five white-bordered photos at slight angles on a wooden table, under the note Next year: more sea, fewer screens',
			caption:
				'A vision board: borders, rotation and overlap, no design tool needed',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'A vision board that doesn’t look like a grid',
				p: [
					'Your new tab page is probably the surface you see most often. Dozens of views a day. No app to open. Five or six pictures at different sizes, overlapping a little, a text widget with the goal across the middle. Ten minutes of work.',
					'Rotation arrived in 4.0, and it’s what makes the polaroid look work. Grab the handle above a picture and turn it; it snaps to 15° steps unless you hold Alt. The Border panel adds a frame up to 12 px wide and rounds the corners, while Bring to front and Send to back in the ⋮ menu decide what overlaps what.',
				],
			},
			{
				h2: 'GIFs and short looping video',
				p: [
					'GIF search, powered by GIPHY, sits in the same picker as the photos. One small loop makes a page feel alive; three competing loops turn it into a slot machine.',
					'Paste a link to an mp4 or webm into Image URL and it plays inside the frame, muted and on a loop. Good for a short clip that a GIF would turn into mush.',
				],
			},
			{
				h2: 'Where the pictures come from',
				p: [
					'Double-click the widget to open the full background picker with all eight tabs. Collections, Wallpapers from wallhaven.cc, Image search on Unsplash, GIF search, Image URL, Upload, Solid color and Favorite images. Wallpapers asks for access to wallhaven.cc the first time you open it.',
					'Uploads (JPG, PNG, WEBP or GIF, 10 MB at most) stay in this browser. A theme stores only a reference, so whoever installs yours gets the layout with an empty frame. Pictures from search or a URL travel fine.',
					'Pictures are requested at the size of your screen, scaled for high-density displays up to 1.5×, so frames stay sharp without pulling giant files.',
				],
			},
			{
				h2: 'Pictures that change by themselves',
				p: [
					'Take a picture from Collections and it changes on its own, on a period you set in this widget’s picker. That period is separate from the page background, so a small frame can cycle every hour while the wallpaper holds still for a week.',
					'For a vision board, pick single pictures and they stay put. For a decorative frame? Let it roam.',
				],
			},
		],
		settings: [
			{
				name: 'Picture',
				detail:
					'Double-click for the eight-tab picker, the same one the page background uses.',
			},
			{
				name: 'Change period',
				detail:
					'Set in the widget’s own picker, independent of the wallpaper.',
			},
			{
				name: 'Border',
				detail:
					'Solid, dashed or dotted frame up to 12 px, opacity, rounded corners.',
			},
			{
				name: 'Rotate',
				detail: 'Handle or button, anywhere from −180° to 180°.',
			},
			{
				name: 'Layers',
				detail: 'Bring to front and Send to back, in the ⋮ menu.',
			},
		],
		useCases: [
			'A vision board for the year, every picture turned a few degrees.',
			'A mood board for a design project.',
			'A small frame of changing photos beside a clock.',
			'A single looping GIF as the only decoration on a plain page.',
			'A muted clip of the place you’re saving up to visit.',
		],
		faq: [
			{
				q: 'How do I make a vision board on my Chrome new tab?',
				a:
					'A vision board on Chrome’s new tab is a handful of Image & GIF widgets from New Tab Studio, one picture each, sized, tilted and overlapped until it looks like a board. Rotation snaps to 15° steps unless you hold Alt, the Border panel adds a white polaroid frame, and a text widget across the middle names the goal.',
			},
			{
				q:
					'Can I put a GIF on my new tab without making it the background?',
				a:
					'Yes, an Image & GIF widget holds a GIF in its own frame anywhere on the page, and the wallpaper stays untouched. Find one in GIF search, which runs on GIPHY, paste any GIF link, or upload a file of up to 10 MB. One small loop livens a page up; three start fighting for attention.',
			},
			{
				q: 'Can I put a video on my Chrome new tab?',
				a:
					'Yes, the Image & GIF widget plays a video from a link to an mp4 or webm file, muted and on a loop, inside its frame. Video files can’t be uploaded, only images can. Want the clip behind the whole page instead? Paste the same link into the background picker’s Image URL tab.',
			},
			{
				q: 'Can I build a vision board for free?',
				a:
					'Yes, though a free vision board holds three pictures of your own, or two and a caption, since each frame is a widget and the free plan allows three added by hand. Frames that arrive in an installed theme don’t use that allowance. GIFs, video, borders and rotation are free either way.',
			},
			{
				q: 'If I share my theme, do my uploaded pictures go with it?',
				a:
					'No, uploaded pictures stay in your browser and a shared theme only points at them, so whoever installs it sees an empty frame. That’s good for privacy and awkward for sharing. Use a picture from search or a URL when you want it to travel.',
			},
		],
		related: ['wallpaper', 'text', 'figure', 'quotes'],
		keywords: [
			'vision board chrome extension',
			'new tab gif',
			'mood board new tab',
			'image widget new tab page',
		],
	},

	{
		slug: 'quotes',
		name: 'Quotes',
		category: 'Design',
		plan: 'free',
		model: 'QuotesModel',
		title: 'Motivational Quotes Chrome Extension',
		metaDescription:
			'Motivational quotes on your new tab in 10 languages: 11 categories, your own lines, seven designs and a new quote anywhere from every tab to once a week.',
		h1: 'Motivational quotes for your new tab, in 10 languages',
		lead:
			'Motivational quotes on your new tab, or funny ones, or only lines you wrote yourself. A quote you see once is a nice sentence; one you see forty times a day starts to argue with you. So the setting that matters most is how often it changes.',
		screenshot: {
			file: 'quotes-hero.webp',
			alt:
				'Six quote widgets in different designs: card, editorial serif, bold poster, handwritten, typewriter and minimal',
			caption: 'Same widget, six designs, from tidy card to typewriter',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'How often it changes',
				p: [
					'Change the quote offers New tab, 5 min, Hour, 6 hours, Day and Week. Six hours is the default. That’s about three quotes in a working day.',
					'New tab turns it into a different widget. At six hours you read a line and chew on it for the afternoon, whereas with a fresh one every time you get a stream, and before long you catch yourself opening tabs partly to see what it says next. Week is the other extreme — one sentence to live with.',
				],
			},
			{
				h2: 'Pinning and your own lines',
				p: [
					'Pin a quote and it stays until you unpin it or page past it yourself, in which case the next one gets pinned instead. The pinned line is saved as text, so changing language or categories won’t swap it out from under you.',
					'My quotes is a category of its own. Run it next to the built-in ones, or on its own for a page nobody else would recognise. Liked quotes live in your general settings rather than the theme, so a rebuilt layout keeps them.',
					'The newer designs put Like, Previous, Pin, Next and Copy right on the quote.',
				],
			},
			{
				h2: 'Eleven categories, ten languages',
				p: [
					'Classic, Motivation, Success & work, Wisdom, Funny, Love & friendship, Life, Movies & books, Science, Sport & health and Learning. Settings show them as tiles with a count on each, so you can see how big the pool you’re choosing is.',
					'English has 975 quotes. Russian, German, Spanish, French, Italian, Portuguese, Turkish, Japanese and Chinese each get their own set of about 134, with local classics rather than a translated English list. Switch the interface to German and Goethe, Schiller and Kant turn up.',
					'A catch. With a non-English interface, the widget draws only from that language’s set and English isn’t mixed in, so the pool is smaller.',
				],
			},
			{
				h2: 'Seven designs',
				p: [
					'Classic, Card, Editorial, Poster, Handwritten, Typewriter and Minimal. A new widget gets Card, which labels each quote with its category. Long quotes shrink to fit the frame instead of spilling over.',
					'Authors can be shown, hidden or linked to Wikipedia in your interface language. That link opens only when you click it, and the widget fetches nothing to show a quote.',
				],
			},
			{
				h2: 'A quote without the widget',
				p: [
					'Settings → General → Popups has an Inspiration popup that shows a quote over the page, no widget required. Choose Once a day, Sometimes or Every new tab (it starts Off). Sometimes means at most once an hour, on about 15 percent of new tabs.',
					'It closes on a click or after 12 seconds, and "Don’t show again" is right there. Since it is not a widget, it doesn’t use up any of your three free ones.',
				],
				image: {
					file: 'quotes-popup.webp',
					alt:
						'German inspiration popup with a proverb over a blurred wallpaper and a Don’t show again button',
					caption:
						'A quote on every new tab, here in German. Nicht mehr anzeigen turns it off',
					width: 1440,
					height: 900,
					small: true,
				},
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Classic, Card, Editorial, Poster, Handwritten, Typewriter or Minimal.',
			},
			{
				name: 'Categories',
				detail:
					'Eleven built in, plus My quotes and Liked quotes, each tile with its count.',
			},
			{
				name: 'Change the quote',
				detail: 'New tab, 5 min, Hour, 6 hours, Day or Week.',
			},
			{
				name: 'Pin the current quote',
				detail: 'It stays until you unpin it or switch it yourself.',
			},
			{
				name: 'Show the author',
				detail:
					'With an optional "Link the author to Wikipedia" switch.',
			},
			{ name: 'Show the category', detail: 'Card design only.' },
			{
				name: 'Inspiration popup',
				detail:
					'In the general settings, not the widget. Off, Once a day, Sometimes or Every new tab.',
			},
		],
		useCases: [
			'A rotating line across the bottom of a photo theme.',
			'One pinned sentence for the length of a project.',
			'Your own lines only, a category nobody else would recognise.',
			'A fresh quote on every tab as a reason to keep opening the page.',
			'Quotes in the language you’re learning, by switching the interface to it.',
		],
		faq: [
			{
				q:
					'Is there a Chrome extension that shows a quote every time I open a new tab?',
				a:
					'Yes, New Tab Studio’s Quotes widget shows a fresh quote on every new tab once Change the quote is set to New tab. Out of the box it changes every 6 hours, and the other choices are 5 minutes, an hour, a day or a week. Rather skip the widget? The Inspiration popup under Settings → General can show a quote on every tab too.',
			},
			{
				q: 'Can I add my own quotes to the new tab?',
				a:
					'Yes, your own quotes go into My quotes, a category of its own with an author for each line. Run it next to the 11 built-in categories, or alone so the page shows only lines you chose. Liked quotes live in your general settings rather than the theme, so they survive a redesign.',
			},
			{
				q: 'Can I get quotes in a language other than English?',
				a:
					'Yes, the quotes widget covers 10 languages. English has 975 quotes, while German, Spanish, Japanese and six others have about 134 each, drawn from local classics rather than translations. The widget follows the interface language and uses only that set, so switch the interface to English for the full collection.',
			},
			{
				q: 'Is there a free motivational quotes extension for Chrome?',
				a:
					'Yes, motivational quotes on the new tab are free in New Tab Studio, with Motivation among 11 categories and all seven designs unlocked. The Inspiration popup isn’t a widget and uses no slot at all, while the Quotes widget takes one of the three you can add on the free plan.',
			},
			{
				q: 'Does the quotes widget work offline?',
				a:
					'Yes, every quote ships inside the extension, so the widget fetches nothing to show one and works without a connection. The only link out is the author’s Wikipedia page, which opens when you click it and not before.',
			},
		],
		related: ['text', 'image', 'habit-tracker', 'wallpaper'],
		keywords: [
			'motivational quotes chrome extension',
			'quotes new tab',
			'daily quote chrome extension',
			'motivational quotes new tab',
		],
	},

	{
		slug: 'weather',
		name: 'Weather',
		category: 'Design',
		plan: 'free',
		model: 'WeatherModel',
		title: 'Weather Chrome Extension with Forecast',
		metaDescription:
			'A weather widget for Chrome’s new tab: current conditions, an hourly or daily forecast, feels-like and wind, six designs. No location permission needed.',
		h1: 'Weather widget for Chrome’s new tab',
		lead:
			'A weather Chrome extension that sits on your new tab answers the jacket question before you think to ask it. Since 4.0 it forecasts hours or days ahead too. It still never asks where you are.',
		screenshot: {
			file: 'weather-hero.webp',
			alt:
				'Four forecast cards: hourly for Lisbon and Vancouver, daily for Kyoto and Reykjavik',
			caption:
				'Next hours or next days, a different setting for each city',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Six designs, one of them a forecast',
				p: [
					'Classic, Glass, Minimal, Sky, Forecast and Compact, with animated icons drawn by the extension itself. A new widget arrives as Glass.',
					'Forecast shows five columns, either Next hours or Next days. It is the only design that fetches a forecast, and it refreshes that no more than once every three hours. Current conditions update hourly in all of them.',
					'Glass and Sky add "Feels like, humidity and wind". Show details is on from the start and can be turned off.',
				],
			},
			{
				h2: 'What each design lets you hide',
				p: [
					'Classic works the old way: icon, temperature, a one-word condition (Clear, Clouds, Rain) and the city, each with its own switch. The newer designs keep the temperature fixed and spell the condition out as a phrase, "Light rain" or "Scattered clouds", in your interface language.',
					'Glass lets you hide the icon, description, city and details. Minimal hides the icon, description and city; Sky the description, city and details; Forecast and Compact the description and city.',
					'Units are °C, °F or K — Kelvin is there for the physicists.',
				],
				image: {
					file: 'weather-glass.webp',
					alt:
						'Glass weather card for Sydney with feels-like, humidity and wind next to a night sky card for New York in Fahrenheit',
					caption:
						'Noon in Sydney, evening in New York: the sky card follows the local time',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Your city, without location access',
				p: [
					'The extension has no location permission at all. A new widget guesses the city from your system time zone, so someone on America/Argentina/Buenos_Aires starts with Buenos Aires, and the settings window opens so you can put it right.',
					'Until the first reading lands you see a placeholder, never somebody else’s weather. A reading older than three hours gets a small stale mark; a failed update offers Try again.',
					'Each widget keeps its own city. A second one can watch where your family lives, or where you’re flying next week.',
				],
			},
			{
				h2: 'Where the data goes',
				p: [
					'Readings come straight from OpenWeatherMap, not through our servers. Analytics notes that you changed the city, never which one. Offline, the widget keeps its last reading and marks it as out of date.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail: 'Classic, Glass, Minimal, Sky, Forecast or Compact.',
			},
			{
				name: 'City',
				detail:
					'Search as you type. A new widget starts from your time zone’s city.',
			},
			{
				name: 'Forecast',
				detail: 'Next hours or Next days, in the Forecast design.',
			},
			{
				name: 'Show details',
				detail: 'Feels like, humidity and wind, in Glass and Sky.',
			},
			{ name: 'Units', detail: '°C, °F or K.' },
		],
		useCases: [
			'A corner readout that settles the jacket question.',
			'Next days in the Forecast design above a trip countdown.',
			'A second widget for the city where family lives.',
			'Feels-like and wind for anyone heading out on a bike.',
		],
		faq: [
			{
				q: 'Is there a free weather widget for Chrome?',
				a:
					'Yes, New Tab Studio has a free weather widget for Chrome’s new tab, with current conditions every hour and an hourly or daily forecast in the Forecast design. All six designs come free, the forecast included. It never asks for your location; the city is guessed from your time zone and you can change it.',
			},
			{
				q: 'How do I add weather to my Chrome new tab page?',
				a:
					'Weather goes on the Chrome new tab through a new tab extension with a weather widget. In New Tab Studio, hit the button at the bottom left, open the Common group and pick Weather. Settings open straight away with a city guessed from your time zone, and a placeholder shows until the first reading lands, never someone else’s weather.',
			},
			{
				q: 'Can I customize how the weather widget looks?',
				a:
					'Yes, the weather widget comes in six designs (Classic, Glass, Minimal, Sky, Forecast and Compact) and reads in °C, °F or Kelvin. Glass and Sky add feels-like, humidity and wind, and every design lets you hide the description or the city. A second widget can follow another city entirely.',
			},
			{
				q:
					'Where does the weather data come from, and does it work offline?',
				a:
					'Weather readings come straight from OpenWeatherMap, not through our servers, and analytics only notes that you changed the city, never which one. Offline, the widget keeps its last reading, and once that’s older than three hours a small mark says it’s out of date.',
			},
		],
		related: ['clock', 'date', 'iframe', 'currency'],
		keywords: [
			'weather chrome extension',
			'weather widget chrome',
			'new tab weather',
			'chrome new tab weather widget',
		],
	},

	{
		slug: 'figure',
		name: 'Figures & Lines',
		category: 'Design',
		plan: 'free',
		model: 'FigureModel',
		title: 'Shapes & Lines Widget for New Tab',
		metaDescription:
			'Rectangles, lines, circles and pills for your new tab layout. See-through fills, dashed or dotted borders, rotation, and dividers as thin as two pixels.',
		h1: 'Shapes and lines for your new tab layout',
		lead:
			'Every other widget shows something. This one shows nothing, and it’s the reason a page looks designed rather than assembled: the rule under a heading, the panel behind a column, the line between work and home.',
		screenshot: {
			file: 'figure-panels.webp',
			alt:
				'Border panel open above a frosted rectangle with a dashed outline, next to a tilted yellow pill and a thin line',
			caption:
				'Dashed, 3 px, 60% opacity, rounded 24: the whole border lives in one panel',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Grouping without a grid',
				p: [
					'Widgets float freely. That’s the best thing about the extension and its biggest risk, because eight unorganised widgets read as eight strangers. A translucent rectangle behind three of them says "these go together" faster than any amount of careful alignment.',
					'Dashboard themes lean on this constantly. Put one panel behind the work column and another behind the personal side, and suddenly each group reads as a single block instead of a scatter of loose parts. Cheap trick. Works.',
				],
				image: {
					file: 'drag-in-progress.webp',
					alt:
						'A weather card being dragged in edit mode, with a selection frame, rotate handle and guide lines lining it up with the clock',
					caption:
						'Drag a widget and guides show when it lines up with its neighbors',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'One rectangle, several shapes',
				p: [
					'There is exactly one figure, a rectangle, and the menu calls it "Square / Line" because those are the two things people make with it. Squash it and you have a line. In 4.0 a side can go down to 2 px, where it used to stop at 16, so a hairline divider finally looks like one.',
					'Circles need a little arithmetic — the corner slider goes to 64 px, and the number field next to it takes up to 200. A square up to 128 px becomes a circle with the slider; up to 400 px if you type 200. Anything larger stays a rounded square.',
				],
			},
			{
				h2: 'Transparency, borders, rotation',
				p: [
					'A solid block on a photo hides the photo. The same block at fifteen percent darkens it just enough for white text to read, and you keep the picture. The fill palette has an alpha channel for exactly that.',
					'The Border panel adds a solid, dashed or dotted outline from 0 to 12 px, with its own opacity and 18 preset colours. Then there’s rotation (±180°, snapping to 15° steps), which turns a short rounded bar into a tilted pill for a label to sit on.',
				],
			},
		],
		settings: [
			{
				name: 'Shape',
				detail:
					'One rectangle. Flatten it for a line, round it off for a pill or a circle.',
			},
			{
				name: 'Fill',
				detail:
					'Any colour at any opacity, the key to panels that work over photos.',
			},
			{
				name: 'Border',
				detail:
					'Solid, dashed or dotted, 0 to 12 px, 18 colours or No border.',
			},
			{
				name: 'Rounded corners',
				detail: 'Slider to 64 px, or type up to 200 px.',
			},
			{
				name: 'Rotate',
				detail:
					'By button or handle, snapping to 15° unless you hold Alt.',
			},
			{
				name: 'Send to back',
				detail:
					'In the ⋮ menu, so the panel sits under the widgets it groups.',
			},
		],
		useCases: [
			'Translucent panels grouping related widgets on a dashboard.',
			'A 2 px rule under a heading or between two columns.',
			'A darkening band behind text on a bright photo.',
			'A tilted pill behind a label or a link.',
		],
		faq: [
			{
				q: 'What’s the best way to organize widgets on my new tab?',
				a:
					'Group widgets on the new tab by putting a translucent rectangle behind each group, so the page reads as a few blocks instead of a scatter. The Square / Line widget in New Tab Studio does exactly that — its fill takes any opacity, and at around 15 percent it frames a group without hiding the wallpaper. Send it to back from the ⋮ menu.',
			},
			{
				q: 'How do I add a thin divider line to my new tab?',
				a:
					'Add the Square / Line widget from the Common group and shrink its height, since a side can go down to 2 px. The widget even tells you so ("To make a horizontal line just decrease the height"). For a vertical rule, shrink the width instead, or turn the line with the rotate handle.',
			},
			{
				q: 'Can I make circles with the shapes widget?',
				a:
					'Yes, a circle is a square with its corners rounded all the way. The corner slider stops at 64 px, which turns squares up to 128 px into circles, and typing 200 into the number field stretches that to 400 px. Anything bigger stays a rounded square.',
			},
			{
				q: 'Do shapes and lines count toward the free widget limit?',
				a:
					'Yes, every shape you place yourself is a widget, so it counts toward the three the free plan allows, and a layout heavy on panels and dividers fills that fast. Shapes that came with an installed theme are exempt. Fill, border, rotation and the 2 px minimum cost nothing either way.',
			},
		],
		related: ['text', 'image', 'wallpaper', 'link'],
		keywords: [
			'new tab shapes widget',
			'divider new tab page',
			'custom new tab layout blocks',
		],
	},

	{
		slug: 'iframe',
		name: 'Iframe Embed',
		category: 'Design',
		plan: 'free',
		model: 'IframeModel',
		title: 'Iframe Widget: Embed a Page on New Tab',
		metaDescription:
			'Embed a web page on your Chrome new tab: a dashboard, a map, NASA’s solar system, a live wind map. Paste a URL or a whole embed code, size it like any widget.',
		h1: 'Iframe widget: embed any page on your new tab',
		lead:
			'Every other widget does one thing. The iframe widget embeds a page on your new tab and does whatever that page does, which makes it the escape hatch for everything the rest don’t cover.',
		screenshot: {
			file: 'iframe-hero.webp',
			alt:
				'Windy wind map of Europe and NASA Eyes on the Solar System embedded on the page next to a sailing task list',
			caption:
				'Two live websites sitting on the page like any other widget',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Ready-made embeds in the menu',
				p: [
					'The IFrame group in the widget menu has six tiles. Custom address, Wikipedia Random article, Interesting fact, Solar system (NASA), Ambient sounds and Wind map, the last three new in 4.0. Solar system brings in NASA’s viewer from eyes.nasa.gov, Ambient sounds loads A Soft Murmur, and Wind map is Windy’s wind layer.',
					'The colour palette and currency converter tiles are gone, because both sites refuse to be embedded and the tiles would stay empty. For exchange rates there’s now a proper currency widget. Google Docs, Sheets and Slides moved to the Productivity group.',
				],
				image: {
					file: 'iframe-menu.webp',
					alt:
						'Edit mode widget panel with the IFrame group open: six tiles from Custom address to Wind map',
					caption:
						'Six ready-made embeds, or paste any address that allows framing',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Your calendar: embed it or sync it',
				p: [
					'Google Calendar used to be the main reason to reach for an iframe. There are two tidier routes today. The Google Calendar widget embeds it for you, and the built-in calendar reads your Google Calendar through its private iCal address, read-only, syncing every 30 minutes.',
					'The plain iframe is still there for everything else. A monitoring dashboard, a status board, a departure board for the stop outside.',
				],
			},
			{
				h2: 'Pasting an embed code',
				p: [
					'Sites that offer embedding hand you an HTML snippet rather than an address. Paste the snippet as is and the address is pulled out of its src="…"; leave off the http:// and it gets added.',
					'In edit mode the frame is frozen. Clicks don’t reach the page inside, so you can grab the widget anywhere and drag it.',
				],
			},
			{
				h2: 'Pages that stay blank',
				p: [
					'Plenty of sites forbid being shown inside another page. Facebook, CNN and news sites stay empty, and the settings window warns you up front. The extension does not alter what sites send back, so no switch gets round it. The examples offered in settings are Wikipedia, OpenStreetMap and Windy.',
					'Mind the cost too. An embed loads its page every time you open a tab — a heavy web app makes your new tab exactly as slow as that app. One light embed on the workspace that needs it, given real room, is the sweet spot.',
				],
			},
		],
		settings: [
			{
				name: 'URL',
				detail:
					'An address or a whole embed snippet, saved a moment after you stop typing.',
			},
			{
				name: 'Examples',
				detail:
					'Wikipedia random, OpenStreetMap and Windy, one click each.',
			},
			{
				name: 'Menu presets',
				detail:
					'Six tiles in the IFrame group, from Custom address to Wind map.',
			},
			{
				name: 'Size and position',
				detail:
					'Placed like any widget. Give it space; a calendar squeezed into 300 px is unreadable.',
			},
		],
		useCases: [
			'A metrics or monitoring dashboard on a work workspace.',
			'Ambient sounds playing while you work.',
			'A live wind map before a sailing or cycling weekend.',
			'A departure board or status page.',
		],
		faq: [
			{
				q: 'Can I embed a website in my Chrome new tab?',
				a:
					'Yes, the iframe widget in New Tab Studio embeds a web page on Chrome’s new tab, as long as that site allows being shown inside other pages. Paste an address or a whole embed code and the address gets pulled out of its src. The menu also has six ready-made tiles, NASA’s Solar system and a live Wind map among them.',
			},
			{
				q: 'Why does the website I embedded show up blank?',
				a:
					'An embedded website shows up blank when the site forbids being displayed inside other pages, and browsers respect that. Facebook, CNN and many news sites do it, and the settings window warns about them. The extension doesn’t alter what sites send back, so put a link widget to that page there instead.',
			},
			{
				q: 'Will an embedded page slow down my new tab?',
				a:
					'An embedded page can slow the new tab down, because the iframe widget loads it fresh every time you open a tab. A light page like a map or a status board barely registers. A heavy web app makes the new tab exactly as slow as that app, so one light embed per workspace is a sensible limit.',
			},
			{
				q: 'Is embedding a page on the new tab free?',
				a:
					'Yes, embedding is free, and so are all six tiles in the IFrame menu group, from Custom address to Wind map. Each embed you add uses one of three free widget places, while embeds that came inside a theme don’t count.',
			},
		],
		related: ['google-calendar', 'calendar', 'workspaces', 'weather'],
		keywords: [
			'iframe new tab chrome',
			'embed page new tab',
			'embed website new tab',
			'dashboard new tab extension',
		],
	},

	{
		slug: 'wallpaper',
		name: 'Wallpaper & Background',
		category: 'Design',
		plan: 'free',
		model: 'BackgroundSelector',
		title: 'Live Wallpaper for Chrome: Video, GIF, 4K',
		metaDescription:
			'New tab wallpaper from eight sources: 4K photos from wallhaven, GIFs, looping mp4 video, your uploads, solid colours. A Chrome wallpaper extension, built in.',
		h1: 'New tab wallpaper: 4K photos, GIFs and live video',
		lead:
			'New tab wallpaper is the part of the page you can’t avoid looking at, and New Tab Studio works as a full Chrome wallpaper extension for it. Eight sources, from 4K wallhaven photos to GIFs and looping video. It also decides whether your widgets stay readable, so it earns five minutes of thought.',
		screenshot: {
			file: 'wallpaper-picker.webp',
			alt:
				'Background picker on the Wallpapers tab: wallhaven categories, a Full HD filter and an Every 6 hours shuffle bar',
			caption:
				'Wallhaven categories, and a shuffle that changes the wallpaper every few hours',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Eight tabs, eight kinds of background',
				p: [
					'Collections (a new photo from a theme you pick, changed automatically), Wallpapers, Image search on Unsplash, GIF search on GIPHY, Image URL, Upload, Solid color and Favorite images.',
					'Solid color got a real palette in 4.0. There are 16 popular colours, a Material palette and a custom colour with an eyedropper and a HEX field. Plain colours only, no gradients.',
					'Holiday sets are in Collections under Holidays (Christmas, Halloween, Thanksgiving day, Birthday, Holiday). They download like every other collection, so a holiday page takes two minutes, not an image hunt.',
				],
				image: {
					file: 'wallpaper-color.webp',
					alt:
						'Solid color tab of the background picker with popular colors, a full palette and a custom HEX field',
					caption: 'Sometimes a wallpaper is just a nice color',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Wallpapers from wallhaven, up to 4K',
				p: [
					'The Wallpapers tab is new in 4.0. Twenty categories, from Nature, Mountains and Space to Minimal, Anime and Japan, sorted by Popular, Random or Newest, with a search inside each category.',
					'A resolution filter keeps soft images out. Full HD, 2K or 4K and up, exact sizes, Ultrawide 21:9, or My screen and up, which counts your monitor’s physical pixels with scaling included. Everything is landscape, safe for work and at least 1920×1080.',
					'Shuffle sets a random wallpaper from the category and turns on auto-change, and every next one comes from the same category. The tab asks for access to wallhaven.cc the first time you open it.',
				],
				image: {
					file: 'wallpaper-category.webp',
					alt:
						'Mountains category with a grid of 4K wallpapers and the resolution filter open on My screen and up',
					caption:
						'Pick a category, then filter by what your screen can actually show',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Live wallpaper and GIFs',
				p: [
					'Live wallpaper for Chrome, here, means a video link — paste an mp4 or webm address into Image URL and it plays behind the widgets, muted and looping; ogv, m4v and mov links work too, and the video is checked before it’s set.',
					'Two limits. A video file cannot be uploaded (Upload takes images), and a video background never auto-changes.',
					'Want a GIF for Chrome background instead? Search GIPHY in GIF search, paste any GIF link into Image URL, or upload one of your own.',
				],
				image: {
					file: 'chrome-background-video.webp',
					alt:
						'Background picker with an mp4 link pasted into the URL field, over a new tab whose video background shows drifting clouds',
					caption:
						'Paste an mp4 link and the clouds start moving behind your widgets',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Auto-change, pin, going back',
				p: [
					'Every 5 minutes, hour, 6 hours (the default), 24 hours or week, or Custom from one minute to 30 days. There is no "don’t change" entry in that list. To keep one picture, press the pin in the bottom panel.',
					'Rotation carried off one you liked? Show previous image steps back through the last ten. Like a picture and it lands in Favorite images, where Share gallery gives you a link to your collection on new-tab.studio.',
					'Sometimes you only want the photo. The button at the bottom left hides every widget and remembers it until you bring them back.',
				],
			},
			{
				h2: 'A background your widgets survive',
				p: [
					'Your text sits in the upper third and the centre — the image needs to be calm there. Dark photo, light text is the most forgiving pair. Readability gets fixed on the widgets, not on the photo. Text has a shadow, a stroke and a fill; for a whole group, slide a translucent shape widget behind it.',
					'With several workspaces, they all share one background until you turn on "Only for workspace N" in this window. A background set that way stays fixed.',
				],
			},
		],
		settings: [
			{
				name: 'Sources',
				detail:
					'Collections, Wallpapers, Image search, GIF search, Image URL, Upload, Solid color, Favorite images.',
			},
			{
				name: 'Auto-change period',
				detail:
					'Every 5 minutes, hour, 6 hours, 24 hours, week, or Custom from 1 minute to 30 days.',
			},
			{
				name: 'Resolution filter',
				detail:
					'In Wallpapers. Full HD, 2K or 4K and up, exact sizes, Ultrawide 21:9 or My screen and up.',
			},
			{
				name: 'Image URL',
				detail:
					'Any picture, or an mp4, webm, ogv, m4v or mov video played muted on a loop.',
			},
			{
				name: 'Only for workspace N',
				detail:
					'Gives one workspace its own picture; Use the common background reverts it.',
			},
			{
				name: 'Favorites and history',
				detail:
					'Liked pictures, Share gallery, and the last ten backgrounds.',
			},
		],
		useCases: [
			'Rotating 4K photos from one wallhaven category.',
			'A looping video behind a page you leave open on a second monitor.',
			'A single GIF as the whole mood of the page.',
			'A solid colour for maximum legibility on a work dashboard.',
			'Holiday pictures behind a countdown to the big day.',
		],
		faq: [
			{
				q: 'Can I put a GIF as my Chrome background?',
				a:
					'Yes, though not through Chrome’s own Customize Chrome panel, which is made for still pictures; an animated GIF background takes a new tab extension. New Tab Studio’s background picker lets you search GIPHY in the GIF search tab, paste any GIF link into Image URL or upload a GIF of up to 10 MB. It then plays behind all your widgets.',
			},
			{
				q: 'Can I use a video as a live wallpaper in Chrome?',
				a:
					'Yes, New Tab Studio plays a video as a live wallpaper behind your widgets, muted and looping, from a link pasted into the Image URL tab. It takes mp4 and webm, plus ogv, m4v and mov. A video file can’t be uploaded, and a video background stays put rather than auto-changing.',
			},
			{
				q: 'Where can I get 4K wallpapers for my new tab?',
				a:
					'High-resolution wallpapers, 4K included, come from the Wallpapers tab of the background picker, which pulls images from wallhaven.cc in 20 categories, from Nature and Space to Anime and Japan. A resolution filter can hold out for 4K and up, or My screen and up, and Shuffle keeps changing the picture within one category. The tab asks for access to wallhaven.cc first.',
			},
			{
				q: 'How do I stop my new tab background from changing?',
				a:
					'To stop the new tab background from changing, press the pin in the bottom panel of the page and the current picture stays. The auto-change list itself has no "don’t change" entry, only periods from every 5 minutes to every week, plus Custom. Show previous image steps back through the last ten if one you liked has gone.',
			},
			{
				q: 'Do I have to pay for 4K wallpapers or video backgrounds?',
				a:
					'No, every background source is free, wallhaven wallpapers, GIFs and video links included. The wallpaper isn’t a widget either, so it never eats into the three widgets the free plan lets you add. Giving each workspace a background of its own costs nothing too.',
			},
		],
		related: ['image', 'figure', 'text', 'workspaces'],
		keywords: [
			'new tab wallpaper',
			'chrome wallpaper extension',
			'live wallpaper for chrome',
			'gif for chrome background',
			'new tab background chrome',
		],
	},

	{
		slug: 'currency',
		name: 'Currency & Crypto',
		category: 'Design',
		plan: 'free',
		model: 'CurrencyModel',
		title: 'Currency Converter Chrome Extension',
		metaDescription:
			'Exchange rates and crypto prices on your Chrome new tab: 31 currencies, 24 coins, a set amount to convert, change arrows, five designs. New in version 4.0.',
		h1: 'Currency converter and crypto prices on your new tab',
		lead:
			'A currency converter Chrome extension usually hides behind a toolbar button. This one lives on the new tab page, so rates and coin prices are already on screen before you think to check. Chrome Web Store users got it with version 4.0.',
		screenshot: {
			file: 'currency-hero.webp',
			alt:
				'Currency widget with four cards: dollar, pound, bitcoin and ether in euros with change arrows, next to a clock',
			caption: 'Dollars, pounds and coins in one widget, arrows included',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Convert a set amount',
				p: [
					'Pick the currency you think in, then up to ten rows of currencies and coins against it. Set Amount to 100 and every currency row shows what 100 of your base is worth. Handy if you invoice a fixed sum or budget a trip.',
					'It won’t replace a calculator. Changing the amount means a trip to settings, so for the odd one-off sum or a bit of arithmetic, a calculator widget parked right beside it will be quicker every time.',
				],
			},
			{
				h2: 'Crypto prices in the same table',
				p: [
					'24 coins sit alongside 31 currencies, each shown as "1 BTC = …" in your base currency. Coins pick their own precision by price, so bitcoin doesn’t get eight decimals and a tiny coin doesn’t round to zero. Currencies take a Decimals setting from 0 to 4.',
					'An arrow and a percentage sit by every number. For a currency it’s the change since the last update; for a coin, the last 24 hours. A glance is enough.',
					'Coin prices arrive in dollars and get converted, so switching the base currency re-prices them without a new request. Show inverse rates flips the currency rows when the other direction reads better.',
				],
			},
			{
				h2: 'Five designs',
				p: [
					'Classic (the table), Cards, Glass, Ticker and Minimal. A new widget comes up as Cards and opens the currency picker straight away. Ticker runs the rates as a scrolling line — trading-floor style.',
					'There is no font size control. Resize the widget and the numbers follow.',
				],
				image: {
					file: 'currency-skins.webp',
					alt:
						'Five currency widget designs with the same rates: classic table, cards, glass, minimal and a ticker',
					caption: 'Same rates, five looks',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Where the numbers come from',
				p: [
					'Currency rates come from Frankfurter every six hours, with open.er-api.com standing by, while coins come from CoinGecko every 15 minutes, with Binance as the spare. Two halves, each with a spare. They load separately, so one provider having a bad morning doesn’t blank the other.',
					'The widget checks for fresh numbers every five minutes and skips that entirely while the tab sits hidden in the background.',
					'Keep it small and off to one side. You want to notice a move, not stare at a ticker forty times a day.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail: 'Classic, Cards, Glass, Ticker or Minimal.',
			},
			{
				name: 'Base currency',
				detail: 'The one every row is expressed in.',
			},
			{
				name: 'Currencies & coins',
				detail:
					'31 currencies and 24 coins to choose from, ten rows at most.',
			},
			{
				name: 'Amount',
				detail:
					'"The rates are shown for this amount." Default 1; coins stay at one coin.',
			},
			{
				name: 'Show inverse rates',
				detail: 'Turns each currency pair the other way round.',
			},
			{ name: 'Decimals', detail: '0 to 4, for currencies.' },
		],
		useCases: [
			'Freelancers invoicing in a currency they don’t live in.',
			'Earning in one currency and paying rent in another.',
			'What 500 of your money buys abroad, before the trip.',
			'A couple of coins with their 24-hour change, checked in passing.',
		],
		faq: [
			{
				q: 'Is there a currency converter for the Chrome new tab?',
				a:
					'Yes, the Currency & crypto widget in New Tab Studio puts exchange rates for 31 currencies on Chrome’s new tab, up to ten rows at once. Set Amount to 100 and each currency row shows what 100 of your base currency is worth. For odd one-off sums, a calculator widget parked alongside is quicker.',
			},
			{
				q: 'Can I see crypto prices on my Chrome new tab?',
				a:
					'Yes, the Currency & crypto widget tracks 24 coins in the same table as regular currencies, each priced per coin in your base currency (1 BTC = …). Beside every coin sits its change over the last 24 hours. Coin prices refresh every 15 minutes from CoinGecko, with Binance as a backup.',
			},
			{
				q: 'How often do exchange rates update in the widget?',
				a:
					'Exchange rates in the currency widget refresh every six hours, from Frankfurter with open.er-api.com as a backup, while coin prices refresh every 15 minutes. The two halves load separately, so one provider having a bad day doesn’t blank the other. Offline, the widget keeps showing the last figures it fetched.',
			},
			{
				q: 'Are the currency rates and crypto prices free?',
				a:
					'Yes, currency rates, all 24 coins, the Amount setting and the five designs come at no charge. The widget itself occupies one of three free spots for hand-added widgets, unless it arrived as part of an installed theme.',
			},
		],
		related: ['calculator', 'weather', 'notes', 'workspaces'],
		keywords: [
			'currency converter chrome extension',
			'crypto price chrome extension',
			'exchange rates new tab',
			'crypto prices new tab',
		],
	},

	{
		slug: 'canvas',
		name: 'Canvas',
		category: 'Design',
		plan: 'free',
		model: 'CanvasModel',
		title: 'Drawing Extension for Chrome New Tab',
		metaDescription:
			'Sketch on your Chrome new tab with a pen, arrows and boxes. Nine colours, four widths, undo, room that grows as you resize. It draws on the new tab only.',
		h1: 'Canvas: a drawing extension for your new tab',
		lead:
			'Looking for a drawing extension for Chrome? Canvas is one for your new tab page, and only there; it won’t draw over other websites. Three boxes and an arrow explain a plan in four seconds, and this is where those four seconds get kept. It reached Chrome Web Store users in version 4.0.',
		screenshot: {
			file: 'canvas-hero.webp',
			alt:
				'Freehand sketch over a sunset sea: three boxes, yellow and blue arrows, wavy lines and a circled box',
			caption:
				'A quick diagram from the standup, drawn right on the new tab',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Pen, arrow, rectangle, cursor',
				p: [
					'Three drawing tools cover the grammar of a whiteboard sketch. The pen for anything, arrows for what points where, rectangles for the things being pointed at. Nine colours plus one of your own, four widths (2, 4, 8 and 14).',
					'The cursor is the fourth tool — it selects a shape, moves it and drags an arrow’s ends. Delete removes the selection, Undo last shape takes back a slip, and Clear the canvas asks before it wipes everything. Done or Esc gets you out.',
				],
			},
			{
				image: {
					file: 'canvas-edit.webp',
					alt:
						'Canvas in edit mode: drawing toolbar, Cursor tool active, a yellow arrow selected with handles at both ends',
					caption:
						'Grab an arrow by its ends; the rest of the page dims while you draw',
					width: 1440,
					height: 900,
					small: true,
				},
				h2: 'Drawing mode is separate from moving',
				p: [
					'Out of edit mode the canvas behaves like any other widget, and you drag it around. Drawing happens in Edit, which dims the rest of the page.',
					'The two gestures are the same gesture. Without the split, every attempt to move the widget would leave a line across your diagram, and every line would drag the widget. One extra click keeps them apart.',
				],
			},
			{
				h2: 'Resizing changes the paper, not the drawing',
				p: [
					'Make the widget bigger and you get more room, not a blown-up sketch. Your boxes keep the size you drew them, with fresh space around them for the next bit.',
					'Rectangles are filled at 35 percent, so they tint rather than cover. Put an image widget with a screenshot underneath and you can box the part that matters without hiding it.',
				],
			},
			{
				h2: 'What it won’t do',
				p: [
					'It will not draw on other websites. The canvas is a widget on your new tab page, and that’s the only place it lives.',
					'No text tool, no circles, no eraser beyond Undo and Delete. It is not a design app and doesn’t try to be. The real design app just isn’t open on your new tab, and this is.',
				],
			},
		],
		settings: [
			{
				name: 'Tools',
				detail:
					'Pen, Arrow, Rectangle, and a Cursor for selecting and moving.',
			},
			{ name: 'Colour', detail: 'Nine presets or your own.' },
			{ name: 'Width', detail: '2, 4, 8 or 14.' },
			{
				name: 'Edit mode',
				detail: 'Where drawing happens; the rest of the page dims.',
			},
			{
				name: 'Undo last shape / Clear the canvas',
				detail: 'One step back, or a clean sheet after a confirmation.',
			},
			{
				name: 'Size',
				detail:
					'Resizing changes the room to draw, not the size of what’s drawn.',
			},
		],
		useCases: [
			'A diagram sketched while someone explains something on a call.',
			'The shape of a problem you haven’t finished thinking about.',
			'A rough plan of a room, a route or a layout.',
			'Boxing the important part of a screenshot in an image widget.',
		],
		faq: [
			{
				q: 'Is there a drawing extension for the Chrome new tab?',
				a:
					'Yes, the Canvas widget in New Tab Studio lets you sketch right on Chrome’s new tab with a pen, arrows and rectangles, in nine colours plus one of your own. It’s a whiteboard for quick diagrams rather than a design app — no text tool, no circles and no eraser beyond Undo and Delete.',
			},
			{
				q: 'Can I draw on any website, not just the new tab?',
				a:
					'No, the Canvas widget draws only inside its own frame on the new tab page and never over other sites. Marking up web pages calls for a different kind of extension. On the new tab, though, an image widget underneath lets you box parts of a screenshot, since rectangles fill at 35 percent.',
			},
			{
				q: 'Why do I have to click Edit before drawing?',
				a:
					'Drawing on the canvas happens in Edit mode because dragging to draw and dragging to move the widget are the same gesture. Without the split, every move would leave a line across your sketch. In Edit the rest of the page dims, and Done or Esc takes you back out.',
			},
			{
				q: 'Can I sketch on the new tab for free?',
				a:
					'Yes, Canvas is free with every tool, all four widths (2, 4, 8 and 14) and Undo. It needs no internet either, since the drawing is saved with the extension’s data. As with any widget you add yourself, it counts toward the free plan’s three.',
			},
		],
		related: ['figure', 'notes', 'image', 'text'],
		keywords: [
			'drawing extension chrome',
			'draw on new tab page',
			'sketch chrome new tab',
			'whiteboard new tab extension',
		],
	},
];
